import { BulkUploadAction } from "store/actions";
import { IAction } from "store/actions/i-action";

import {
  BulkFileExtractionStatus,
  IBulkBatch,
  IBulkBatchFile,
  IBulkFileStatusEvent
} from "../../types";
import { reducer } from "../index";

const TERMINAL_STATUSES: BulkFileExtractionStatus[] = ["done", "failed", "unsupported", "cancelled"];

const isTerminalFileStatus = (status: BulkFileExtractionStatus): boolean => TERMINAL_STATUSES.includes(status);

const completedCount = (files: IBulkBatchFile[]): number => files.filter((f) => isTerminalFileStatus(f.status)).length;

/** Replace the whole batch map from a REST snapshot (source of truth). */
reducer.createHandler(BulkUploadAction.SetBatches, (state, { payload }: IAction<IBulkBatch[]>) => ({
  ...state,
  bulkUpload: {
    batches: payload.reduce<Record<string, IBulkBatch>>((acc, batch) => {
      acc[batch.upload_batch_id] = batch;
      return acc;
    }, {})
  }
}));

/** Apply a per-file status delta (Pusher). Creates the batch group if unseen. */
reducer.createHandler(BulkUploadAction.ApplyFileStatus, (state, { payload }: IAction<IBulkFileStatusEvent>) => {
  const { uploadBatchId, fileId, fileName, dealId, status } = payload;
  const existing = state.bulkUpload.batches[uploadBatchId];

  const fileRow: IBulkBatchFile = {
    file_id: fileId,
    file_name: fileName,
    deal_id: dealId,
    status,
    created_at: existing?.files.find((f) => f.file_id === fileId)?.created_at ?? "",
    updated_at: ""
  };

  const files = existing
    ? existing.files.some((f) => f.file_id === fileId)
      ? existing.files.map((f) => (f.file_id === fileId ? { ...f, status } : f))
      : [...existing.files, fileRow]
    : [fileRow];

  const batch: IBulkBatch = {
    upload_batch_id: uploadBatchId,
    deal_id: existing?.deal_id ?? dealId,
    deal_name: existing?.deal_name ?? "",
    batch_status: existing?.batch_status ?? "in_progress",
    files,
    file_count: Math.max(existing?.file_count ?? 0, files.length),
    completed_count: completedCount(files)
  };

  return {
    ...state,
    bulkUpload: { batches: { ...state.bulkUpload.batches, [uploadBatchId]: batch } }
  };
});

/** Mark a batch ready for review (batch-awaiting-review event). */
reducer.createHandler(BulkUploadAction.MarkAwaitingReview, (state, { payload }: IAction<string>) => {
  const existing = state.bulkUpload.batches[payload];
  if (!existing) return state;
  return {
    ...state,
    bulkUpload: {
      batches: { ...state.bulkUpload.batches, [payload]: { ...existing, batch_status: "awaiting_review" } }
    }
  };
});

/** Remove a batch (dismissed locally or via batch-dismissed event). */
reducer.createHandler(BulkUploadAction.RemoveBatch, (state, { payload }: IAction<string>) => {
  if (!state.bulkUpload.batches[payload]) return state;
  const { [payload]: _removed, ...rest } = state.bulkUpload.batches;
  return { ...state, bulkUpload: { batches: rest } };
});

reducer.createHandler(BulkUploadAction.Clear, (state) => ({
  ...state,
  bulkUpload: { batches: {} }
}));
